import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import OrderwiseIntegrationForm from './OrderwiseIntegrationForm.tsx';
import { GET_ORDERWISE_INTEGRATIONS, UPDATE_ORDERWISE_INTEGRATION } from './queries.ts';
import type { OrderwiseFormData, OrderwiseIntegration, UpdateOrderwiseIntegrationResult } from './types.ts';
import { parseDecimalField } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_ORDERWISE_PATH } from '../../constants.ts';

const FORM_ID = 'orderwise-integration-form';

const OrderwiseIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const { publishToast } = useToaster();

    const backTooltipText = `${t('backTooltipText')} ${t('orderwise.title')}`;

    const { data, loading, refetch } = useQuery(GET_ORDERWISE_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.orderwiseIntegrations?.find((i) => i.id === integrationId);

    const [updateIntegration] = useMutation<UpdateOrderwiseIntegrationResult>(UPDATE_ORDERWISE_INTEGRATION);

    // Only the integration fields are saved here; the output-mapper config is saved independently by the
    // OutputMapperEditorPanel (via the generic updateOutputMapperConfig mutation) on its own Save button.
    const handleUpdateIntegration = async (
        formData: OrderwiseFormData,
        onSuccess?: (updated: OrderwiseIntegration) => void
    ) => {
        if (!integration) return;

        try {
            const result = await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    username: formData.username,
                    password: formData.password,
                    baseUrl: formData.baseUrl,
                    sessionId: formData.sessionId,
                    priceCheckLowerThresholdPercentage: parseDecimalField(formData.priceCheckLowerThresholdPercentage),
                    priceCheckUpperThresholdPercentage: parseDecimalField(formData.priceCheckUpperThresholdPercentage),
                    integrationParameters: formData.integrationParameters ?? {},
                    proxy: formData.proxy,
                    attachmentsBaseUrl: formData.attachmentsBaseUrl || null,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                },
            });

            await refetch();

            const updatedIntegration = result.data?.updateOrderwiseIntegration?.integration;
            if (onSuccess && updatedIntegration) {
                onSuccess(updatedIntegration);
            }

            publishToast({ description: t('orderwise.updateSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('orderwise.updateError'), status: 'error' });
        }
    };

    if (!loading && !integration) {
        return (
            <Layout>
                <SettingsNavigation />
                <Page className="flex-1">
                    <Page.Content lowered>
                        <p className="text-content-medium">{t('orderwise.noIntegrations')}</p>
                        <Button type="button" asChild className="mt-4">
                            <Link to={url(CUSTOMER_INTEGRATION_ORDERWISE_PATH)}>
                                {t('orderwise.form.cancelButton')}
                            </Link>
                        </Button>
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('orderwise.title'), to: url(CUSTOMER_INTEGRATION_ORDERWISE_PATH) },
                            { label: integration?.name || t('orderwise.form.updateTitle') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ORDERWISE_PATH)}>
                                {t('orderwise.form.cancelButton')}
                            </Link>
                        </Button>
                        <Button variant="primary" type="submit" form={FORM_ID} disabled={loading || !integration}>
                            {t('orderwise.form.updateButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    {!loading && integration && (
                        <OrderwiseIntegrationForm
                            integration={integration}
                            onSubmit={handleUpdateIntegration}
                            formId={FORM_ID}
                        />
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default OrderwiseIntegrationEditPage;
